import { useState } from "react";
import { X } from "lucide-react";

const COMPANY = {
  name: "TTW Engineering",
  address: "Wartelstraat 16, 8223EH Lelystad",
  kvk: "42022089",
  btw: "NL005441251B40",
  email: "ttw.engineering26@gmail.com",
  phone: "+31 6 38617073",
};

const TermsEN = () => (
  <div className="space-y-6 text-[#B0B0B0] text-sm leading-relaxed">
    <h2 className="text-2xl text-white font-semibold">General Terms and Conditions</h2>
    <p className="text-[#8A8A8A] text-xs">Last updated: June 2026</p>

    <section>
      <h3 className="text-lg text-white mb-2">1. Company Information</h3>
      <p>{COMPANY.name}<br/>Address: {COMPANY.address}<br/>KVK: {COMPANY.kvk}<br/>BTW: {COMPANY.btw}<br/>Email: {COMPANY.email}<br/>Phone: {COMPANY.phone}</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">2. Definitions</h3>
      <p>"Company" refers to {COMPANY.name}. "Customer" refers to any individual or entity that engages the Company's services. "Vehicle" refers to the motor vehicle on which services are performed. "Tuning" refers to any modification, remapping, or alteration of the vehicle's engine control unit (ECU), transmission control unit (TCU), or any related electronic or mechanical component. "Services" refers to all services offered by the Company including but not limited to chiptuning, ECU remapping, diagnostics, and special requests.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">3. Applicability</h3>
      <p>These terms and conditions apply to all offers, quotations, agreements, and services provided by {COMPANY.name}. By engaging our services, the Customer agrees to be bound by these terms in full. Any deviations from these terms are only valid if explicitly agreed upon in writing.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">4. Manufacturer Warranty Disclaimer</h3>
      <p><strong className="text-white">The Customer acknowledges and agrees that any form of chiptuning, ECU remapping, or engine modification may void the vehicle's manufacturer warranty in part or in full.</strong> The Company is not responsible for any loss of warranty coverage resulting from services performed. It is the Customer's sole responsibility to verify the impact of tuning on their vehicle's warranty before proceeding with any service.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">5. Limitation of Liability</h3>
      <p><strong className="text-white">The Company shall not be held liable for any direct, indirect, incidental, consequential, or special damages arising from or related to the services provided.</strong> This includes but is not limited to:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Engine, transmission, drivetrain, or any mechanical failure or damage</li>
        <li>Turbocharger, intercooler, exhaust system, or intake system failure</li>
        <li>Clutch, flywheel, or gearbox damage resulting from increased power/torque</li>
        <li>Electrical system failures or ECU/TCU malfunctions</li>
        <li>Increased fuel consumption or emissions</li>
        <li>Failure to pass vehicle inspection (APK/MOT) or emissions testing</li>
        <li>Loss of use of the vehicle</li>
        <li>Towing, rental car, or any consequential costs</li>
        <li>Loss of income or business opportunities</li>
        <li>Personal injury or property damage resulting from vehicle use after modification</li>
        <li>Any damage caused by pre-existing mechanical or electrical issues in the vehicle</li>
        <li>Any damage resulting from improper use, racing, or abuse of the vehicle after modification</li>
      </ul>
      <p className="mt-2">The Customer agrees that all tuning and modifications are performed at the Customer's own risk. The maximum liability of {COMPANY.name} in any circumstance shall not exceed the amount paid by the Customer for the specific service in question.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">6. Customer Responsibility and Acknowledgment</h3>
      <p>By engaging the Company's services, the Customer explicitly acknowledges and agrees that:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>The vehicle is in proper mechanical condition and suitable for the requested modifications</li>
        <li>The Customer has disclosed all known mechanical, electrical, or performance issues with the vehicle</li>
        <li>The Customer assumes full responsibility for any consequences arising from the modification of their vehicle</li>
        <li>The Customer is responsible for ensuring the vehicle complies with all applicable laws, regulations, and road safety standards after modification</li>
        <li>Chiptuning may affect vehicle insurance coverage — the Customer is responsible for informing their insurance company</li>
        <li>The Customer will not hold the Company liable for any damage, loss, or injury resulting from the use of a modified vehicle</li>
        <li>Stage 2 and higher tuning levels require appropriate hardware upgrades, and the Customer is responsible for ensuring these are properly installed</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">7. Performance Data Disclaimer</h3>
      <p><strong className="text-white">All performance figures, power gains, and torque increases displayed on this website are indicative estimates only.</strong> Actual results may vary depending on the vehicle's condition, mileage, fuel quality, altitude, ambient temperature, hardware modifications, and other factors. {COMPANY.name} does not guarantee specific performance outcomes. The data provided serves as a general reference and should not be relied upon as a guaranteed result.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8. Vehicle Inspection and Pre-existing Conditions</h3>
      <p>The Company may perform a basic diagnostic check before commencing work. However, this does not constitute a full mechanical inspection. The Company is not responsible for identifying or disclosing pre-existing conditions that may be affected by tuning. If the Company determines that a vehicle is not suitable for the requested service, it reserves the right to refuse service without liability.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8a. ECU Drop-off and Transport</h3>
      <p><strong className="text-white">For tuning methods that require bench or boot mode access, the ECU must be physically removed from the vehicle.</strong> The Customer is required to drop off the ECU at our location after making an appointment. If the Customer chooses to send the ECU by post or courier service, all transport is entirely at the Customer's own risk. {COMPANY.name} accepts no liability for any damage, loss, or delay caused by careless handling during transport by third-party postal or courier services.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">9. Right to Refuse Service</h3>
      <p>{COMPANY.name} reserves the right to refuse any service at its sole discretion, including but not limited to cases where the vehicle is deemed unsafe, mechanically unsound, or where the requested modification would be illegal or unethical.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">10. Payment Terms</h3>
      <p>All services must be paid in full before or upon completion unless otherwise agreed in writing. Prices are exclusive of VAT unless stated otherwise. The Company reserves the right to retain the vehicle until full payment has been received. Accepted payment methods are <strong className="text-white">bank transfer and cash only</strong> for tuning services. For the webshop, payment is accepted via <strong className="text-white">iDEAL</strong>.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">11. Webshop — Right of Withdrawal (14-Day Cooling-Off Period)</h3>
      <p>In accordance with Dutch and European consumer protection law, the Customer has the right to cancel an online purchase within <strong className="text-white">14 calendar days</strong> after receiving the product, without providing a reason. To exercise this right, the Customer must notify {COMPANY.name} in writing (by email to {COMPANY.email}) within this 14-day period.</p>
      <p className="mt-2"><strong className="text-white">After the 14-day cooling-off period has expired, the right of withdrawal lapses entirely and irrevocably.</strong> No returns, exchanges, or refunds will be accepted or processed after this period, regardless of the reason.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Products must be returned unused, undamaged, and in their original packaging</li>
        <li>Products that have been installed, modified, used, or show signs of wear cannot be returned</li>
        <li>Return shipping costs are always at the Customer&apos;s expense</li>
        <li>Refunds will be processed within 14 days after {COMPANY.name} has received the returned product and confirmed it meets the above conditions</li>
        <li>{COMPANY.name} reserves the right to deduct a depreciation charge if the product has been handled beyond what is necessary to establish its nature, characteristics, and functioning</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">12. Webshop — Incorrectly Ordered Products</h3>
      <p><strong className="text-white">The Customer is solely responsible for verifying that the ordered products are compatible with their vehicle and suitable for their intended purpose before placing an order.</strong> {COMPANY.name} provides product descriptions, specifications, and vehicle compatibility information as a guide, but the Customer bears full responsibility for their purchase decision.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>{COMPANY.name} shall not be held liable for products ordered in error, including wrong fitment, wrong specifications, wrong size, or wrong vehicle application</li>
        <li>If a Customer orders a product that does not fit their vehicle, {COMPANY.name} is under no obligation to accept a return or offer a refund outside the 14-day cooling-off period</li>
        <li>Returns of incorrectly ordered products within the 14-day period are only accepted if the product is unused and in original packaging; return shipping costs are at the Customer&apos;s expense</li>
        <li>Custom-ordered, special-order, or made-to-measure products are excluded from the right of withdrawal and cannot be returned</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">13. Webshop — Defective Products and Warranty Claims</h3>
      <p><strong className="text-white">Any defect, damage, or discrepancy must be reported to {COMPANY.name} in writing within 14 calendar days of receiving the product.</strong> Failure to report within this period shall be considered acceptance of the product in its received condition, and the Customer waives all rights to a claim, exchange, or refund.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Upon delivery, the Customer must inspect the product for visible damage or defects. Delivery acceptance without written complaint constitutes proof of receipt in good condition</li>
        <li>Claims for defects discovered after the 14-day inspection period will not be accepted under any circumstances</li>
        <li>Products that have been installed, modified, or used in any way are excluded from defect claims, as installation constitutes acceptance of the product</li>
        <li>{COMPANY.name} does not provide a separate warranty on products sold through the webshop beyond the manufacturer&apos;s warranty (if applicable). Any warranty claim must be directed to the original manufacturer</li>
        <li><strong className="text-white">{COMPANY.name} shall not be held liable for any consequential damage, loss of use, installation costs, labour costs, or any other costs resulting from a defective product</strong></li>
        <li>The maximum liability of {COMPANY.name} for any product-related claim shall not exceed the purchase price of the specific product in question</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">14. Webshop — Shipping Damage</h3>
      <p>The Customer must inspect all packages immediately upon delivery. If the package shows visible damage upon delivery, the Customer must:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Note the damage on the delivery receipt or refuse the package</li>
        <li>Photograph the damage (packaging and contents)</li>
        <li>Report the damage to {COMPANY.name} within <strong className="text-white">2 working days</strong> of delivery by email with photos</li>
      </ul>
      <p className="mt-2"><strong className="text-white">Claims for shipping damage reported after 2 working days will not be accepted.</strong> {COMPANY.name} is not liable for damage that could have been identified upon delivery but was not reported in time.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">15. Webshop — Exclusion of Liability After Acceptance</h3>
      <p><strong className="text-white">Once the 14-day cooling-off and inspection period has expired without a written complaint, the product is deemed accepted by the Customer in all respects.</strong> After acceptance, {COMPANY.name} shall not be held liable for:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Any defects, whether visible or hidden, that were not reported within the 14-day period</li>
        <li>Any damage resulting from improper installation, use, or storage of the product</li>
        <li>Any incompatibility with the Customer&apos;s vehicle or other components</li>
        <li>Normal wear and tear</li>
        <li>Any loss of performance, revenue, or use resulting from a product issue</li>
        <li>Any costs incurred by the Customer for installation, removal, replacement, or repair</li>
        <li>Any claims by third parties arising from the Customer&apos;s use of the product</li>
      </ul>
      <p className="mt-2">The Customer agrees that {COMPANY.name} acts as a reseller of third-party performance parts. {COMPANY.name} does not manufacture the products and therefore cannot be held liable for manufacturing defects. Any warranty or quality claims beyond the 14-day period must be addressed directly to the product manufacturer.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">16. No Refund Policy (Tuning Services)</h3>
      <p><strong className="text-white">All services provided by {COMPANY.name} are final and non-refundable.</strong> Due to the nature of ECU remapping and chiptuning services, which involve the creation and application of custom software specifically tailored to the Customer's vehicle, the delivered service constitutes bespoke digital content that cannot be "returned" or "undone" in the traditional sense. In accordance with EU Directive 2011/83/EU (Consumer Rights Directive), Article 16(a), the right of withdrawal does not apply to contracts for the supply of goods or services made to the Customer's specifications or clearly personalized.</p>
      <p className="mt-2">By engaging our services, the Customer expressly acknowledges and agrees that:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Each tuning file is a custom software product created specifically for the Customer's vehicle, ECU type, and hardware configuration</li>
        <li>The service is fully performed upon completion of the ECU remapping, at which point the custom software has been delivered and applied</li>
        <li>The Customer waives any right to a cooling-off period or refund once the tuning service has commenced</li>
        <li>No refunds will be issued for dissatisfaction with performance gains, as results are indicative and may vary</li>
        <li>No refunds will be issued for any mechanical issues, warranty loss, or insurance implications arising after the service</li>
      </ul>
      <p className="mt-2">In cases where the Company determines a genuine technical error was made during the tuning process, the Company may, at its sole discretion, offer to re-perform the service or restore the original ECU mapping. This does not constitute an obligation to provide a refund.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">17. Intellectual Property</h3>
      <p>All tuning files, software, and technical data used or created by {COMPANY.name} remain the intellectual property of the Company and/or its suppliers. Customers may not copy, distribute, reverse-engineer, or resell any tuning files or proprietary data.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">18. Force Majeure</h3>
      <p>The Company shall not be liable for any delay or failure to perform obligations due to circumstances beyond its reasonable control, including but not limited to natural disasters, pandemics, supply chain disruptions, government actions, or technical failures.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">19. Legal Compliance</h3>
      <p>The Customer is solely responsible for ensuring that any modifications performed on their vehicle comply with all applicable local, national, and EU laws and regulations. This includes but is not limited to emissions standards, road safety regulations, and vehicle registration requirements. The Company does not guarantee that tuned vehicles will pass APK (Dutch vehicle inspection) or equivalent inspections in other countries.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">20. Insurance</h3>
      <p>The Customer is responsible for informing their vehicle insurance provider of any modifications made to the vehicle. Failure to do so may result in voided insurance coverage. {COMPANY.name} accepts no liability for any insurance claims that are denied due to undisclosed vehicle modifications.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">21. Indemnification</h3>
      <p>The Customer agrees to indemnify, defend, and hold harmless {COMPANY.name}, its owners, employees, and affiliates from any and all claims, damages, losses, costs, and expenses (including legal fees) arising from or related to the Customer's use of the modified vehicle or any breach of these terms.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">22. Governing Law and Disputes</h3>
      <p>These terms and conditions are governed by the laws of the Netherlands. Any disputes arising from these terms or the services provided shall be submitted exclusively to the competent court in Lelystad, the Netherlands.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">23. Amendments</h3>
      <p>{COMPANY.name} reserves the right to amend these terms and conditions at any time. The most current version will always be available on this website. Continued use of our services after changes constitutes acceptance of the amended terms.</p>
    </section>
  </div>
);

const TermsNL = () => (
  <div className="space-y-6 text-[#B0B0B0] text-sm leading-relaxed">
    <h2 className="text-2xl text-white font-semibold">Algemene Voorwaarden</h2>
    <p className="text-[#8A8A8A] text-xs">Laatst bijgewerkt: juni 2026</p>

    <section>
      <h3 className="text-lg text-white mb-2">1. Bedrijfsgegevens</h3>
      <p>{COMPANY.name}<br/>Adres: {COMPANY.address}<br/>KVK: {COMPANY.kvk}<br/>BTW: {COMPANY.btw}<br/>E-mail: {COMPANY.email}<br/>Telefoon: {COMPANY.phone}</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">2. Definities</h3>
      <p>"Bedrijf" verwijst naar {COMPANY.name}. "Klant" verwijst naar iedere natuurlijke of rechtspersoon die gebruik maakt van de diensten van het Bedrijf. "Voertuig" verwijst naar het motorvoertuig waaraan diensten worden verricht. "Tuning" verwijst naar iedere wijziging, herprogrammering of aanpassing van de motorbesturingseenheid (ECU), transmissiebesturingseenheid (TCU) of gerelateerde elektronische of mechanische componenten.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">3. Toepasselijkheid</h3>
      <p>Deze algemene voorwaarden zijn van toepassing op alle aanbiedingen, offertes, overeenkomsten en diensten van {COMPANY.name}. Door gebruik te maken van onze diensten stemt de Klant volledig in met deze voorwaarden. Afwijkingen zijn alleen geldig indien schriftelijk overeengekomen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">4. Fabrieksgarantie Disclaimer</h3>
      <p><strong className="text-white">De Klant erkent en aanvaardt dat iedere vorm van chiptuning, ECU-herprogrammering of motormodificatie de fabrieksgarantie van het voertuig geheel of gedeeltelijk kan doen vervallen.</strong> Het Bedrijf is niet verantwoordelijk voor verlies van garantiedekking als gevolg van uitgevoerde diensten. Het is de verantwoordelijkheid van de Klant om vooraf de gevolgen voor de garantie te controleren.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">5. Beperking van Aansprakelijkheid</h3>
      <p><strong className="text-white">Het Bedrijf is niet aansprakelijk voor enige directe, indirecte, incidentele, gevolg- of bijzondere schade voortvloeiend uit of verband houdend met de geleverde diensten.</strong> Dit omvat maar is niet beperkt tot:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Motor-, transmissie-, aandrijflijn- of mechanische storingen of schade</li>
        <li>Turbo-, intercooler-, uitlaat- of inlaatsysteemdefecten</li>
        <li>Koppeling-, vliegwiel- of versnellingsbakschade door verhoogd vermogen/koppel</li>
        <li>Elektrische storingen of ECU/TCU-defecten</li>
        <li>Verhoogd brandstofverbruik of emissies</li>
        <li>Niet slagen voor APK-keuring of emissietest</li>
        <li>Verlies van gebruik van het voertuig</li>
        <li>Sleep-, huurauto- of gevolgkosten</li>
        <li>Inkomstenderving of gemiste zakelijke kansen</li>
        <li>Persoonlijk letsel of materiële schade als gevolg van voertuiggebruik na modificatie</li>
        <li>Schade veroorzaakt door reeds bestaande mechanische of elektrische problemen</li>
        <li>Schade als gevolg van oneigenlijk gebruik, racen of misbruik na modificatie</li>
      </ul>
      <p className="mt-2">De Klant stemt ermee in dat alle tuning en modificaties op eigen risico worden uitgevoerd. De maximale aansprakelijkheid van {COMPANY.name} is in alle gevallen beperkt tot het bedrag dat de Klant heeft betaald voor de betreffende dienst.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">6. Verantwoordelijkheid van de Klant</h3>
      <p>Door gebruik te maken van de diensten van het Bedrijf erkent en aanvaardt de Klant uitdrukkelijk dat:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Het voertuig in goede mechanische staat verkeert en geschikt is voor de gevraagde modificaties</li>
        <li>De Klant alle bekende mechanische, elektrische of prestatieproblemen heeft gemeld</li>
        <li>De Klant de volledige verantwoordelijkheid aanvaardt voor alle gevolgen van de modificatie</li>
        <li>De Klant verantwoordelijk is voor naleving van alle wet- en regelgeving na modificatie</li>
        <li>Chiptuning de verzekeringsdekking kan beïnvloeden — de Klant dient zijn verzekeraar te informeren</li>
        <li>De Klant het Bedrijf niet aansprakelijk zal stellen voor schade, verlies of letsel</li>
        <li>Stage 2 en hoger passende hardware-upgrades vereisen, waarvoor de Klant verantwoordelijk is</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">7. Prestatiegegevens Disclaimer</h3>
      <p><strong className="text-white">Alle vermogenscijfers, vermogenswinsten en koppelverhogingen op deze website zijn slechts indicatieve schattingen.</strong> Werkelijke resultaten kunnen variëren afhankelijk van de staat van het voertuig, kilometerstand, brandstofkwaliteit, hoogte, omgevingstemperatuur, hardware-modificaties en andere factoren. {COMPANY.name} garandeert geen specifieke prestatieresultaten.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8. Voertuiginspectie en Bestaande Gebreken</h3>
      <p>Het Bedrijf kan een basisdiagnose uitvoeren voor aanvang van de werkzaamheden. Dit vormt echter geen volledige mechanische inspectie. Het Bedrijf is niet verantwoordelijk voor het vaststellen van reeds bestaande gebreken. Indien het Bedrijf oordeelt dat een voertuig niet geschikt is, behoudt het zich het recht voor de dienst te weigeren.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8a. ECU Aflevering en Transport</h3>
      <p><strong className="text-white">Voor tuningmethodes die bench- of bootmodus vereisen, moet de ECU fysiek uit het voertuig worden verwijderd.</strong> De Klant dient de ECU na het maken van een afspraak af te leveren op onze locatie. Indien de Klant ervoor kiest de ECU per post of koeriersdienst te verzenden, is al het transport volledig voor eigen risico van de Klant. {COMPANY.name} aanvaardt geen aansprakelijkheid voor eventuele schade, verlies of vertraging veroorzaakt door onzorgvuldige behandeling tijdens transport door externe post- of koeriersdiensten.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">9. Recht van Weigering</h3>
      <p>{COMPANY.name} behoudt zich het recht voor om diensten te weigeren naar eigen inzicht, waaronder gevallen waarbij het voertuig als onveilig wordt beschouwd of de gevraagde modificatie illegaal of onverantwoord zou zijn.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">10. Betalingsvoorwaarden</h3>
      <p>Alle diensten dienen volledig te worden betaald voor of bij oplevering, tenzij schriftelijk anders overeengekomen. Prijzen zijn exclusief BTW tenzij anders vermeld. Het Bedrijf behoudt zich het recht voor het voertuig vast te houden tot volledige betaling is ontvangen. Geaccepteerde betaalmethoden zijn <strong className="text-white">bankoverschrijving en contant</strong> voor tuningdiensten. Voor de webshop wordt betaald via <strong className="text-white">iDEAL</strong>.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">11. Webshop — Herroepingsrecht (14 Dagen Bedenktijd)</h3>
      <p>Conform de Nederlandse en Europese consumentenwetgeving heeft de Klant het recht om een online aankoop binnen <strong className="text-white">14 kalenderdagen</strong> na ontvangst van het product te annuleren, zonder opgaaf van reden. Om dit recht uit te oefenen dient de Klant {COMPANY.name} binnen deze termijn schriftelijk op de hoogte te stellen (per e-mail aan {COMPANY.email}).</p>
      <p className="mt-2"><strong className="text-white">Na het verstrijken van de 14 dagen bedenktijd vervalt het herroepingsrecht volledig en onherroepelijk.</strong> Er worden na deze termijn geen retourzendingen, omruilingen of restituties meer geaccepteerd of verwerkt, ongeacht de reden.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Producten dienen ongebruikt, onbeschadigd en in originele verpakking te worden geretourneerd</li>
        <li>Producten die zijn geïnstalleerd, gewijzigd, gebruikt of gebruikssporen vertonen kunnen niet worden geretourneerd</li>
        <li>Retourverzendkosten zijn altijd voor rekening van de Klant</li>
        <li>Restitutie vindt plaats binnen 14 dagen nadat {COMPANY.name} het geretourneerde product heeft ontvangen en gecontroleerd</li>
        <li>{COMPANY.name} behoudt zich het recht voor een waardevermindering in te houden indien het product meer is gebruikt dan noodzakelijk om de aard, kenmerken en werking vast te stellen</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">12. Webshop — Verkeerd Bestelde Producten</h3>
      <p><strong className="text-white">De Klant is zelf verantwoordelijk voor het controleren of de bestelde producten compatibel zijn met zijn/haar voertuig en geschikt voor het beoogde doel, vóór het plaatsen van een bestelling.</strong> {COMPANY.name} biedt productbeschrijvingen, specificaties en voertuigcompatibiliteit als richtlijn, maar de Klant draagt de volledige verantwoordelijkheid voor de aankoopbeslissing.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>{COMPANY.name} is niet aansprakelijk voor foutief bestelde producten, waaronder verkeerde pasvorm, specificaties, maat of voertuigtoepassing</li>
        <li>Indien een Klant een product bestelt dat niet past op zijn/haar voertuig, is {COMPANY.name} niet verplicht een retour te accepteren of restitutie te verlenen buiten de 14 dagen bedenktijd</li>
        <li>Retour van verkeerd bestelde producten binnen de bedenktijd wordt alleen geaccepteerd indien het product ongebruikt is en in originele verpakking; retourverzendkosten zijn voor rekening van de Klant</li>
        <li>Op maat gemaakte, speciaal bestelde of op specificatie geproduceerde producten zijn uitgesloten van het herroepingsrecht en kunnen niet worden geretourneerd</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">13. Webshop — Defecte Producten en Garantieclaims</h3>
      <p><strong className="text-white">Elk defect, beschadiging of afwijking dient binnen 14 kalenderdagen na ontvangst van het product schriftelijk aan {COMPANY.name} te worden gemeld.</strong> Het niet melden binnen deze termijn wordt beschouwd als aanvaarding van het product in de ontvangen staat, en de Klant doet afstand van alle rechten op een claim, omruiling of restitutie.</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Bij levering dient de Klant het product te inspecteren op zichtbare schade of gebreken. Aanvaarding van de levering zonder schriftelijke klacht geldt als bewijs van ontvangst in goede staat</li>
        <li>Claims voor gebreken die na de 14 dagen inspectieperiode worden ontdekt, worden onder geen enkele omstandigheid geaccepteerd</li>
        <li>Producten die zijn geïnstalleerd, gewijzigd of op enige wijze zijn gebruikt, zijn uitgesloten van defectclaims, aangezien installatie geldt als aanvaarding van het product</li>
        <li>{COMPANY.name} biedt geen afzonderlijke garantie op producten verkocht via de webshop, buiten de eventuele fabrieksgarantie. Garantieclaims dienen rechtstreeks bij de fabrikant te worden ingediend</li>
        <li><strong className="text-white">{COMPANY.name} is niet aansprakelijk voor gevolgschade, gebruiksderving, installatiekosten, arbeidskosten of enige andere kosten voortvloeiend uit een defect product</strong></li>
        <li>De maximale aansprakelijkheid van {COMPANY.name} voor productgerelateerde claims is beperkt tot de aankoopprijs van het betreffende product</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">14. Webshop — Transportschade</h3>
      <p>De Klant dient alle pakketten direct bij levering te inspecteren. Indien het pakket zichtbare schade vertoont bij levering, dient de Klant:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>De schade te vermelden op de afleverbon of het pakket te weigeren</li>
        <li>De schade te fotograferen (verpakking en inhoud)</li>
        <li>De schade binnen <strong className="text-white">2 werkdagen</strong> na levering per e-mail met foto&apos;s te melden aan {COMPANY.name}</li>
      </ul>
      <p className="mt-2"><strong className="text-white">Claims voor transportschade die na 2 werkdagen worden gemeld, worden niet geaccepteerd.</strong> {COMPANY.name} is niet aansprakelijk voor schade die bij levering vastgesteld had kunnen worden maar niet tijdig is gemeld.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">15. Webshop — Uitsluiting Aansprakelijkheid na Aanvaarding</h3>
      <p><strong className="text-white">Zodra de 14 dagen bedenk- en inspectieperiode is verstreken zonder schriftelijke klacht, wordt het product geacht door de Klant te zijn aanvaard in alle opzichten.</strong> Na aanvaarding is {COMPANY.name} niet aansprakelijk voor:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Eventuele gebreken, zichtbaar of verborgen, die niet binnen de 14 dagen termijn zijn gemeld</li>
        <li>Schade voortvloeiend uit onjuiste installatie, gebruik of opslag van het product</li>
        <li>Incompatibiliteit met het voertuig van de Klant of andere componenten</li>
        <li>Normale slijtage</li>
        <li>Verlies van prestaties, omzet of gebruik als gevolg van een productprobleem</li>
        <li>Kosten gemaakt door de Klant voor installatie, demontage, vervanging of reparatie</li>
        <li>Claims van derden voortvloeiend uit het gebruik van het product door de Klant</li>
      </ul>
      <p className="mt-2">De Klant stemt ermee in dat {COMPANY.name} optreedt als wederverkoper van performance onderdelen van derden. {COMPANY.name} produceert de producten niet en kan daarom niet aansprakelijk worden gesteld voor fabricagefouten. Eventuele garantie- of kwaliteitsclaims na de 14 dagen termijn dienen rechtstreeks aan de productfabrikant te worden gericht.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">16. Geen Restitutiebeleid (Tuningdiensten)</h3>
      <p><strong className="text-white">Alle diensten van {COMPANY.name} zijn definitief en niet restitueerbaar.</strong> Vanwege de aard van ECU-herprogrammering en chiptuning, waarbij op maat gemaakte software specifiek voor het voertuig van de Klant wordt ontwikkeld en toegepast, betreft de geleverde dienst digitale inhoud op maat die niet kan worden "geretourneerd" of "ongedaan gemaakt". Conform EU-Richtlijn 2011/83/EU (Richtlijn consumentenrechten), Artikel 16(a), is het herroepingsrecht niet van toepassing op overeenkomsten voor de levering van goederen of diensten die volgens specificaties van de Klant zijn vervaardigd of duidelijk gepersonaliseerd zijn.</p>
      <p className="mt-2">Door gebruik te maken van onze diensten erkent en aanvaardt de Klant uitdrukkelijk dat:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Elk tuningbestand een op maat gemaakt softwareproduct is, specifiek ontwikkeld voor het voertuig, ECU-type en hardwareconfiguratie van de Klant</li>
        <li>De dienst volledig is uitgevoerd bij voltooiing van de ECU-herprogrammering, waarmee de maatwerksoftware is geleverd en toegepast</li>
        <li>De Klant afziet van elk recht op een bedenktijd of restitutie zodra de tuningdienst is aangevangen</li>
        <li>Geen restitutie wordt verleend bij ontevredenheid over vermogenswinst, aangezien resultaten indicatief zijn en kunnen variëren</li>
        <li>Geen restitutie wordt verleend voor mechanische problemen, garantieverlies of verzekeringsgevolgen na de dienst</li>
      </ul>
      <p className="mt-2">In gevallen waarin het Bedrijf vaststelt dat een daadwerkelijke technische fout is gemaakt tijdens het tuningproces, kan het Bedrijf naar eigen inzicht aanbieden de dienst opnieuw uit te voeren of de originele ECU-mapping te herstellen. Dit vormt geen verplichting tot restitutie.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">17. Intellectueel Eigendom</h3>
      <p>Alle tuningbestanden, software en technische gegevens blijven eigendom van {COMPANY.name} en/of haar leveranciers. Klanten mogen geen tuningbestanden kopiëren, verspreiden, reverse-engineeren of doorverkopen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">18. Overmacht</h3>
      <p>Het Bedrijf is niet aansprakelijk voor vertraging of het niet nakomen van verplichtingen door omstandigheden buiten zijn macht, waaronder natuurrampen, pandemieën, leveringsproblemen, overheidsmaatregelen of technische storingen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">19. Wettelijke Naleving</h3>
      <p>De Klant is zelf verantwoordelijk voor naleving van alle toepasselijke lokale, nationale en EU-wet- en regelgeving. Dit omvat emissienormen, verkeersveiligheidsvoorschriften en voertuigregistratie-eisen. Het Bedrijf garandeert niet dat getunede voertuigen door de APK-keuring komen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">20. Verzekering</h3>
      <p>De Klant is verantwoordelijk voor het informeren van zijn voertuigverzekeraar over eventuele modificaties. Het niet melden kan leiden tot het vervallen van de verzekeringsdekking. {COMPANY.name} aanvaardt geen aansprakelijkheid voor geweigerde verzekeringsclaims.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">21. Vrijwaring</h3>
      <p>De Klant stemt ermee in {COMPANY.name}, haar eigenaren, medewerkers en gelieerden te vrijwaren tegen alle claims, schade, verliezen, kosten en uitgaven (inclusief juridische kosten) voortvloeiend uit het gebruik van het gemodificeerde voertuig of schending van deze voorwaarden.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">22. Toepasselijk Recht en Geschillen</h3>
      <p>Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden exclusief voorgelegd aan de bevoegde rechter te Lelystad, Nederland.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">23. Wijzigingen</h3>
      <p>{COMPANY.name} behoudt zich het recht voor deze voorwaarden te allen tijde te wijzigen. De meest actuele versie is altijd beschikbaar op deze website.</p>
    </section>
  </div>
);

const PrivacyEN = () => (
  <div className="space-y-6 text-[#B0B0B0] text-sm leading-relaxed">
    <h2 className="text-2xl text-white font-semibold">Privacy Policy</h2>
    <p className="text-[#8A8A8A] text-xs">Last updated: June 2026</p>

    <section>
      <h3 className="text-lg text-white mb-2">1. Data Controller</h3>
      <p>{COMPANY.name}, {COMPANY.address}, KVK: {COMPANY.kvk}, is the data controller responsible for processing your personal data as described in this privacy policy.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">2. Data We Collect</h3>
      <p>We collect the following personal data when you use our contact form:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number (optional)</li>
        <li>Service interest</li>
        <li>Message content</li>
      </ul>
      <p className="mt-2">We do not use cookies for tracking purposes. We do not collect data through analytics services.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">3. Purpose of Processing</h3>
      <p>Your personal data is processed solely for the purpose of:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Responding to your inquiry or service request</li>
        <li>Providing quotes and service information</li>
        <li>Scheduling appointments</li>
        <li>Legal compliance and record-keeping</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">4. Legal Basis (GDPR Art. 6)</h3>
      <p>We process your data based on: (a) your consent when submitting the contact form, (b) the necessity for the performance of a contract, and (c) our legitimate business interests in responding to inquiries.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">5. Data Retention</h3>
      <p>We retain your personal data for a maximum of 2 years after your last contact, or as long as required by law (7 years for financial records). Data is securely deleted after the retention period.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">6. Your Rights</h3>
      <p>Under the GDPR, you have the right to:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Access your personal data</li>
        <li>Rectify inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Restrict or object to processing</li>
        <li>Data portability</li>
        <li>Withdraw consent at any time</li>
        <li>File a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens)</li>
      </ul>
      <p className="mt-2">To exercise your rights, contact us at {COMPANY.email}.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">7. Data Security</h3>
      <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Data is stored on secure servers with encrypted connections.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8. Third-Party Sharing</h3>
      <p>We do not sell, trade, or share your personal data with third parties, except where required by law or with trusted service providers necessary for our operations (e.g., hosting providers), who are bound by data processing agreements.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">9. Changes to This Policy</h3>
      <p>We may update this privacy policy from time to time. The latest version is always available on this website.</p>
    </section>
  </div>
);

const PrivacyNL = () => (
  <div className="space-y-6 text-[#B0B0B0] text-sm leading-relaxed">
    <h2 className="text-2xl text-white font-semibold">Privacybeleid</h2>
    <p className="text-[#8A8A8A] text-xs">Laatst bijgewerkt: juni 2026</p>

    <section>
      <h3 className="text-lg text-white mb-2">1. Verwerkingsverantwoordelijke</h3>
      <p>{COMPANY.name}, {COMPANY.address}, KVK: {COMPANY.kvk}, is de verwerkingsverantwoordelijke voor de verwerking van uw persoonsgegevens zoals beschreven in dit privacybeleid.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">2. Gegevens die wij Verzamelen</h3>
      <p>Wij verzamelen de volgende persoonsgegevens wanneer u ons contactformulier gebruikt:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Naam</li>
        <li>E-mailadres</li>
        <li>Telefoonnummer (optioneel)</li>
        <li>Gewenste dienst</li>
        <li>Berichtinhoud</li>
      </ul>
      <p className="mt-2">Wij gebruiken geen cookies voor tracking. Wij verzamelen geen gegevens via analyticsdiensten.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">3. Doel van Verwerking</h3>
      <p>Uw persoonsgegevens worden uitsluitend verwerkt voor:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Het beantwoorden van uw vraag of dienstaanvraag</li>
        <li>Het verstrekken van offertes en dienstinformatie</li>
        <li>Het inplannen van afspraken</li>
        <li>Wettelijke naleving en administratie</li>
      </ul>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">4. Rechtsgrondslag (AVG Art. 6)</h3>
      <p>Wij verwerken uw gegevens op basis van: (a) uw toestemming bij het invullen van het contactformulier, (b) de noodzaak voor de uitvoering van een overeenkomst, en (c) ons gerechtvaardigd bedrijfsbelang bij het beantwoorden van vragen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">5. Bewaartermijn</h3>
      <p>Wij bewaren uw persoonsgegevens maximaal 2 jaar na uw laatste contact, of zolang wettelijk vereist (7 jaar voor financiële administratie). Gegevens worden na de bewaartermijn veilig verwijderd.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">6. Uw Rechten</h3>
      <p>Op grond van de AVG heeft u het recht op:</p>
      <ul className="list-disc pl-6 mt-2 space-y-1">
        <li>Inzage in uw persoonsgegevens</li>
        <li>Rectificatie van onjuiste gegevens</li>
        <li>Verwijdering van uw gegevens</li>
        <li>Beperking van of bezwaar tegen verwerking</li>
        <li>Gegevensoverdraagbaarheid</li>
        <li>Intrekking van toestemming</li>
        <li>Een klacht indienen bij de Autoriteit Persoonsgegevens</li>
      </ul>
      <p className="mt-2">Neem contact op via {COMPANY.email} om uw rechten uit te oefenen.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">7. Gegevensbeveiliging</h3>
      <p>Wij treffen passende technische en organisatorische maatregelen om uw persoonsgegevens te beschermen tegen ongeoorloofde toegang, wijziging, openbaarmaking of vernietiging.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">8. Delen met Derden</h3>
      <p>Wij verkopen, verhandelen of delen uw persoonsgegevens niet met derden, behalve wanneer wettelijk vereist of met vertrouwde dienstverleners die gebonden zijn aan verwerkersovereenkomsten.</p>
    </section>

    <section>
      <h3 className="text-lg text-white mb-2">9. Wijzigingen in dit Beleid</h3>
      <p>Wij kunnen dit privacybeleid van tijd tot tijd bijwerken. De laatste versie is altijd beschikbaar op deze website.</p>
    </section>
  </div>
);

const PAGES = {
  terms: { en: TermsEN, nl: TermsNL, titleEN: "Terms & Conditions", titleNL: "Algemene Voorwaarden" },
  privacy: { en: PrivacyEN, nl: PrivacyNL, titleEN: "Privacy Policy", titleNL: "Privacybeleid" },
};

export const LegalModal = ({ page, onClose }) => {
  const [lang, setLang] = useState('en');
  const config = PAGES[page];
  if (!config) return null;
  const Content = lang === 'en' ? config.en : config.nl;

  return (
    <div
      data-testid="legal-modal-overlay"
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="legal-modal-content"
        className="bg-[#111111] border border-[#2A2A2A] rounded-lg max-w-3xl w-full p-8 relative"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              data-testid="legal-lang-en"
              onClick={() => setLang('en')}
              className={`px-3 py-1 rounded text-xs uppercase tracking-wider font-medium transition-colors ${
                lang === 'en' ? 'bg-[#FF3B30] text-white' : 'bg-[#1A1A1A] text-[#8A8A8A] hover:text-white'
              }`}
            >
              English
            </button>
            <button
              data-testid="legal-lang-nl"
              onClick={() => setLang('nl')}
              className={`px-3 py-1 rounded text-xs uppercase tracking-wider font-medium transition-colors ${
                lang === 'nl' ? 'bg-[#FF3B30] text-white' : 'bg-[#1A1A1A] text-[#8A8A8A] hover:text-white'
              }`}
            >
              Nederlands
            </button>
          </div>
          <button
            data-testid="legal-modal-close"
            onClick={onClose}
            className="text-[#8A8A8A] hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <Content />
      </div>
    </div>
  );
};

export const LegalLinks = ({ onOpenTerms, onOpenPrivacy }) => (
  <div data-testid="legal-links" className="flex items-center gap-4 text-xs text-[#4A4A4A]">
    <button
      data-testid="open-terms-btn"
      onClick={onOpenTerms}
      className="hover:text-[#FF3B30] transition-colors underline underline-offset-2"
    >
      Terms & Conditions
    </button>
    <span>|</span>
    <button
      data-testid="open-privacy-btn"
      onClick={onOpenPrivacy}
      className="hover:text-[#FF3B30] transition-colors underline underline-offset-2"
    >
      Privacy Policy
    </button>
  </div>
);
